// react
import { useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import { useGatewaysPostBynewGatewayMutation } from '@/api/generated/recalc/endpoints/v1/gateways';
import type { GatewaysPostBynewGatewayApiResponse } from '@/api/generated/recalc/endpoints/v1/gateways';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFAddressAutocomplete, RHFTextField } from '@/components/ui/form';

// relative
import {
  toCreateGatewayFormValues,
  toPostGatewayPayload,
} from './createGatewayForm.mappers';
import { createGatewayFormSchema } from './createGatewayForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

export default function CreateGatewayForm({
  accountId,
  onSuccess,
  onCancel,
}: {
  accountId: number;
  onSuccess: (data: GatewaysPostBynewGatewayApiResponse) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);

  const [postGateway, { isLoading: isCreating }] =
    useGatewaysPostBynewGatewayMutation();

  const schema = createGatewayFormSchema(t);
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toCreateGatewayFormValues(),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    const payload = toPostGatewayPayload(values, accountId);
    const result = await postGateway(payload);

    if (!('error' in result)) {
      onSuccess(result.data);
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="gateway-register-form">
      <Stack spacing={2}>
        <Card variant="outlined" data-testid="gateway-register-form-card">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                data-tour="gateway-name"
                label={t('createGateway.name.label', { ns: 'forms' })}
                disabled={isCreating}
                placeholder={t('createGateway.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createGateway.name.helperText', {
                  ns: 'forms',
                })}
                data-testid="gateway-register-name-input"
              />

              <RHFTextField
                name="rfuId"
                control={control}
                required
                fullWidth
                data-tour="gateway-rfuid"
                label={t('createGateway.rfuId.label', { ns: 'forms' })}
                disabled={isCreating}
                placeholder={t('createGateway.rfuId.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createGateway.rfuId.helperText', {
                  ns: 'forms',
                })}
                data-testid="gateway-register-rfuid-input"
              />

              <RHFAddressAutocomplete
                name="address"
                control={control}
                disabled={isCreating}
                required
                fullWidth
                size="small"
                label={t('createGateway.address.label', { ns: 'forms' })}
                placeholder={t('createGateway.address.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createGateway.address.helperText', {
                  ns: 'forms',
                })}
              />

              <RHFTextField
                name="description"
                control={control}
                fullWidth
                multiline
                minRows={2}
                helperText={t('createGateway.description.helperText', {
                  ns: 'forms',
                })}
                data-tour="gateway-description"
                label={t('createGateway.description.label', { ns: 'forms' })}
                disabled={isCreating}
                placeholder={t('createGateway.description.placeholder', {
                  ns: 'forms',
                })}
                data-testid="gateway-register-description-input"
              />
            </Stack>
          </CardContent>
        </Card>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isCreating}
            data-testid="gateway-register-cancel-button"
          >
            {t('common.cancel')}
          </CancelButton>
          <SubmitButton
            disabled={isCreating}
            data-tour="gateway-submit"
            data-testid="gateway-register-submit-button"
          >
            {isCreating
              ? t('createGateway.submitting', { ns: 'forms' })
              : t('createGateway.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
